import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { SabiasQueNY } from "./nyHelpers";

const CHINATOWN_MAP_EMBED =
	"https://www.google.com/maps/d/embed?mid=1CQfiFWksiW7knH4geDm3MqNuX-kgpjg&ehbc=2E312F";

// Template81: Día 3 Chinatown & Little Italy 2 - Mapa + Little Italy (Page7). Contenido desde page.
const Template81 = ({
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onMapFullscreenChange,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(navigator.onLine);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(navigator.onLine);

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItems = (html) => {
		if (!html) return [];
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const ps = doc.querySelectorAll("p");
		const items = [];
		ps.forEach((p) => {
			const text = (p.textContent ?? p.innerText ?? "").trim();
			if (!text) return;
			const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
			if (emojiMatch)
				items.push({ icono: emojiMatch[1], texto: emojiMatch[2].trim() });
		});
		return items;
	};

	const title = page?.texts?.[0]?.content;
	const littleItalyTitle = page?.texts?.[1]?.content;
	const intro = page?.texts?.[2]?.content;
	const sabiasQueItems = parseSabiasQueItems(page?.texts?.[3]?.content);
	const listRaw = page?.texts?.[4]?.content;
	const yellowCannoli = page?.texts?.[5]?.content;
	const yellowAudrey = page?.texts?.[6]?.content;
	const comoIr = page?.texts?.[7]?.content;

	const listItems = listRaw
		? String(listRaw)
				.split(/\n/)
				.map((s) => s.trim())
				.filter(Boolean)
		: [];

	const mapUrl = page?.maps?.[0] || CHINATOWN_MAP_EMBED;
	const mapaOfflineSrc = getImageSrc("mapa-offline");
	const littleItalySrc = getImageSrc("little-italy");
	const cafeSrc = getImageSrc("cafe-roma");
	const cannoliSrc = getImageSrc("cannoli");
	const badgeSrc = getImageSrc("little-badge");
	const stickerSrc = getImageSrc("little-italy-sticker");
	const logoSrc = getCanvasImageUrl("logo");

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
			@keyframes spin {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	if (fullscreen) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#59c3d8",
					}}
				>
					<Box
						component="button"
						onClick={() => {
							setFullscreen(false);
							onMapFullscreenChange?.(false);
						}}
						sx={{
							backgroundColor: "#fff",
							color: "#59c3d8",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: fontFamily || '"Poppins", sans-serif',
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<Box
					component="iframe"
					title="Mapa grande Chinatown & Little Italy"
					src={mapUrl}
					width="100%"
					height="100%"
					sx={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				backgroundColor: "#f7f7f7",
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": { marginTop: "0px !important", marginBottom: "0px !important" },
				"& a": {
					color: "#007a8d",
					fontWeight: 700,
					textDecoration: "underline",
				},
			}}
		>
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor || "#59c3d8",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: fontFamily || '"Poppins", sans-serif',
					fontSize: "14px",
					fontWeight: 700,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 99999,
				}}
			>
				{title}
				{logoSrc && (
					<img
						src={logoSrc}
						alt="logo"
						style={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</div>

			<div
				data-block-id="map"
				style={{
					flex: 1,
					marginTop: "-70px",
					position: "relative",
					minHeight: 260,
					maxHeight: 400,
					display: "flex",
					flexDirection: "column",
					minWidth: 0,
				}}
			>
				{!loaded && isOnline && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<div
							style={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: "4px solid #59c3d8",
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
							}}
						/>
					</div>
				)}
				{isOnline && (
					<Box
						component="button"
						onClick={() => {
							setFullscreen(true);
							onMapFullscreenChange?.(true);
						}}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: "#59c3d8",
							color: "#fff",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

							border: "none",
							cursor: "pointer",
							fontFamily: fontFamily || '"Poppins", sans-serif',
						}}
					>
						Ver mapa grande
					</Box>
				)}
				<div style={{ flex: 1, minHeight: 0, position: "relative" }}>
					{isOnline ? (
						<Box
							component="iframe"
							title="Mapa Chinatown & Little Italy"
							src={mapUrl}
							width="100%"
							height="100%"
							sx={{
								border: 0,
								display: "block",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
							}}
							loading="lazy"
							onLoad={() => setLoaded(true)}
						/>
					) : mapaOfflineSrc ? (
						<img
							src={mapaOfflineSrc}
							alt="Mapa Chinatown (offline)"
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
							}}
						/>
					) : null}
				</div>
				{badgeSrc && (
					<img
						src={badgeSrc}
						alt="badge"
						style={{
							width: 160,
							position: "absolute",
							bottom: "-10%",
							right: -60,
						}}
					/>
				)}
				{stickerSrc && (
					<img
						src={stickerSrc}
						alt="Little Italy"
						style={{ width: 120, position: "absolute", bottom: 0, left: -20 }}
					/>
				)}
			</div>

			<div style={{ padding: "10px 12px", flex: 1, position: "relative" }}>
				<div data-block-id="text-1">
					{littleItalyTitle && (
						<h3
							style={{
								fontSize: 14,
								fontWeight: 700,
								fontFamily: '"Poppins", sans-serif',
								textTransform: "uppercase",
								marginTop: 2,
							}}
						>
							{littleItalyTitle}
						</h3>
					)}
					{intro && (
						<p
							style={{
								fontSize: 13,
								fontFamily: '"Poppins", sans-serif',
								color: "#444",
							}}
						>
							{intro}
						</p>
					)}
				</div>
				{sabiasQueItems[0] && (
					<div style={{ position: "absolute", top: 0, left: "35%" }}>
						<SabiasQueNY
							icono={sabiasQueItems[0].icono}
							texto={sabiasQueItems[0].texto}
						/>
					</div>
				)}
				{sabiasQueItems[1] && (
					<div style={{ position: "absolute", top: 0, left: "45%" }}>
						<SabiasQueNY
							icono={sabiasQueItems[1].icono}
							texto={sabiasQueItems[1].texto}
						/>
					</div>
				)}

				{(littleItalySrc || listItems.length > 0) && (
					<div
						data-block-id="text-2"
						style={{
							display: "flex",
							alignItems: "flex-start",
							gap: 16,
							marginTop: 12,
							position: "relative",
						}}
					>
						{!isSmallScreen && cannoliSrc && yellowCannoli && (
							<>
								<img
									src={cannoliSrc}
									alt="Cannoli"
									style={{
										width: 100,
										zIndex: 99,
										position: "absolute",
										bottom: -60,
										right: 0,
									}}
								/>
								<div
									style={{
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 0px 16px",
										padding: "10px 12px",
										fontSize: 11,
										fontFamily: '"Poppins", sans-serif',
										color: "#444",
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: 250,
										position: "absolute",
										transform: "translateY(-100%)",
										zIndex: 99,
										bottom: -90,
										right: 80,
									}}
									dangerouslySetInnerHTML={{ __html: yellowCannoli }}
								></div>
							</>
						)}
						{littleItalySrc && (
							<img
								src={littleItalySrc}
								alt="Little Italy"
								style={{
									width: "40%",
									height: 110,
									borderRadius: 12,
									objectFit: "cover",
								}}
							/>
						)}
						<ul
							style={{
								fontSize: 13,
								paddingLeft: 20,
								width: "60%",
								color: "#444",
								fontFamily: '"Poppins", sans-serif',
								marginTop: 0,
								zIndex: 1,
							}}
						>
							{listItems.map((line, i) => (
								<li key={i} style={{ marginBottom: 8 }}>
									{line.includes(":") ? (
										<>
											<strong>{line.split(":")[0]}:</strong>{" "}
											{line.split(":").slice(1).join(":").trim()}
										</>
									) : (
										line
									)}
								</li>
							))}
						</ul>
					</div>
				)}

				<div
					data-block-id="text-3"
					style={{
						marginTop: isSmallScreen ? 120 : 40,
						display: "flex",
						justifyContent: "flex-end",
						width: "100%",
						position: "relative",
					}}
				>
					{cafeSrc && (
						<img
							src={cafeSrc}
							alt="Caffe Roma"
							style={{
								width: 100,
								position: "absolute",
								bottom: isSmallScreen ? "auto" : 0,
								left: -20,
								top: isSmallScreen ? -85 : "auto",
							}}
						/>
					)}
					{yellowAudrey && (
						<div
							style={{
								position: "absolute",
								bottom: isSmallScreen ? "auto" : 75,
								top: isSmallScreen ? -130 : "auto",
								left: 18,
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 16px 0px",
								padding: "10px 12px",
								fontSize: 11,
								fontFamily: '"Poppins", sans-serif',
								color: "#444",
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: 160,
							}}
						>
							{yellowAudrey.includes("!") ? (
								<>
									<strong>{yellowAudrey.split("!")[0]}!</strong>{" "}
									{yellowAudrey.split("!").slice(1).join("!").trim()}
								</>
							) : (
								yellowAudrey
							)}
						</div>
					)}
					{comoIr && (
						<div
							style={{
								backgroundColor: "#e0f7fa",
								border: "3px solid #59c3d8",
								borderRadius: 12,
								padding: "8px 20px",
								fontSize: 12,
								fontFamily: '"Poppins", sans-serif',
								color: "#333",
								width: isSmallScreen ? "100%" : "70%",
							}}
						>
							{comoIr.includes("?") ? (
								<>
									<strong>{comoIr.split("?")[0]}?</strong>
									<br />
									{comoIr.split("?").slice(1).join("?").trim()}
								</>
							) : (
								comoIr
							)}
						</div>
					)}

					{isSmallScreen && cannoliSrc && yellowCannoli && (
						<div style={{ position: "relative" }}>
							<img
								src={cannoliSrc}
								alt="Cannoli"
								style={{
									width: 100,
									position: "absolute",
									zIndex: 99,
									right: -20,
									top: -80,
								}}
							/>
							<div
								style={{
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 0px 16px",
									padding: "10px 12px",
									fontSize: 11,
									fontFamily: '"Poppins", sans-serif',
									color: "#444",
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									width: 140,
									position: "absolute",
									zIndex: 99,
									right: isSmallScreen ? 0 : 30,
									top: -50,
									transform: "translateY(-100%)",
								}}
								dangerouslySetInnerHTML={{ __html: yellowCannoli }}
							></div>
						</div>
					)}
				</div>
			</div>
		</Box>
	);
};

export default Template81;
